// The editor side of the extension API (spec/dashboard.md section 10.4): `CMS`, `Localization`, `Products` and `Objects`
// over the branch catalog and the workspace. Writes land in the workspace as the calling extension's actor group.
import type { Document, Json, Parameter, Template } from '../api/types';
import { baseOf, cloneDocument } from '../content/actions';
import type { ContentCatalog } from '../content/ContentContext';
import type { DocumentLike } from '../content/format';
import { createDocumentDraft, heirsOf, isComponentTarget } from '../content/widgets/context';
import { entityKey, isTempId, type Actor, type Workspace } from '../workspace/Workspace';
import { describeTemplate, expandDocument, normalizeValue, overlayDrafts, paramForKey, resolveTemplateName, unnyIdOf, type Expanded, type ExpandContext, type WriteContext } from './model';

export interface EditorAdapter {
  catalog: ContentCatalog;
  workspace: Workspace;
  /** the server rows of a template family */
  listDocuments: (template: string, includeHeirs: boolean) => Promise<Document[]>;
  /** one server row by id or legacy id */
  getDocument: (idOrUnnyId: string) => Promise<Document | undefined>;
  /**
   * signed URLs of assets of the branch (M7 slice 3, `POST …/assets:sign`): `Objects.*`'s `fileUrl` answers them, since the
   * public content path of a draft asset answers 404 until a pinned release lists it; absent → the public path
   */
  assetUrls?: (ids: string[]) => Promise<Record<string, string | undefined>>;
}

type Obj = Record<string, unknown>;

export class ApiCallError extends Error {}

/** The dispatcher of one extension's calls: `method` is `<Namespace>.<name>`. */
export function createEditorApi(adapter: EditorAdapter, extension: string): (method: string, args: unknown[]) => Promise<unknown> {
  const { catalog, workspace } = adapter;
  const actor: Actor = `extension:${extension}`;
  const label = (doc: DocumentLike) => catalog.label(doc);

  const draftsOfFamily = (family: Set<string>) => workspace.all.filter((d) => d.kind === 'document' && family.has(d.template ?? ''));

  const resolveLocal = (id: string): DocumentLike | undefined => {
    const direct = catalog.resolve(id);
    if (direct) return direct;
    for (const d of workspace.all) {
      if (d.kind !== 'document' || d.status === 'deleted') continue;
      const cur = d.current as unknown as Document;
      if (cur.unnyId !== null && cur.unnyId !== undefined && String(cur.unnyId) === id) return cur;
    }
    return undefined;
  };
  const expandCtx: ExpandContext = {
    catalog,
    resolve: resolveLocal,
    stringKey: (id) => {
      const d = workspace.getDraft(entityKey('string', id));
      if (!d || d.status === 'deleted') return undefined;
      const c = d.current as { group?: string; key?: string };
      return c.group && c.key ? `${c.group}/${c.key}` : undefined;
    },
  };

  const findDocument = async (id: string): Promise<Document | undefined> => {
    const local = resolveLocal(id) as Document | undefined;
    if (local) {
      const draft = workspace.getDraft(entityKey('document', local.id));
      if (draft?.status === 'deleted') return undefined;
      return (draft?.current as unknown as Document | undefined) ?? local;
    }
    const server = await adapter.getDocument(id);
    if (!server) return undefined;
    const draft = workspace.getDraft(entityKey('document', server.id));
    if (draft?.status === 'deleted') return undefined;
    return (draft?.current as unknown as Document | undefined) ?? server;
  };

  const writeCtx: WriteContext = {
    catalog,
    stringIdFor: (fullKey) => {
      const existing = catalog.stringList.find((s) => s.fullKey === fullKey || `${s.group}/${s.key}` === fullKey);
      if (existing) return existing.id;
      const staged = workspace.all.find((d) => d.kind === 'string' && d.status !== 'deleted' && `${(d.current as { group?: string }).group}/${(d.current as { key?: string }).key}` === fullKey);
      if (staged) return staged.id;
      const slash = fullKey.indexOf('/');
      const group = fullKey.slice(0, slash);
      const key = fullKey.slice(slash + 1);
      return workspace.create('string', { id: '', group, key, description: null, export: true, values: { [catalog.defaultLanguage]: key } } as unknown as Json, { label: fullKey, template: 'Localization', actor });
    },
    documentIdFor: (ref) => {
      if (ref === null || ref === undefined) return null;
      if (typeof ref === 'object') {
        const o = ref as { $id?: unknown; $unnyId?: unknown };
        if (typeof o.$id === 'string') return o.$id;
        if (typeof o.$unnyId === 'string') ref = o.$unnyId;
        else return null;
      }
      if (typeof ref !== 'string') return null;
      const found = resolveLocal(ref);
      return found ? found.id : isTempId(ref) || /^[0-9a-f-]{36}$/i.test(ref) ? ref : null;
    },
  };

  const templateOf = (name: string): Template => {
    const t = resolveTemplateName(catalog, name);
    if (!t) throw new ApiCallError(`Unknown template: ${name}`);
    return t;
  };

  const familyOf = (t: Template, includeHeirs: boolean) => new Set([t.name, ...(includeHeirs ? heirsOf(catalog, t.name).map((h) => h.name) : [])]);

  const editField = (doc: Document, p: Parameter, value: Json) => {
    workspace.edit('document', doc.id, `data.${p.name}`, value, { base: isTempId(doc.id) ? undefined : baseOf(doc), label: label(doc), template: doc.template, actor });
  };

  const applyFields = async (doc: Document, template: Template, fields: Obj): Promise<void> => {
    for (const [key, value] of Object.entries(fields)) {
      if (key.startsWith('$')) continue;
      const p = paramForKey(template, key);
      if (!p) throw new ApiCallError(`Unknown parameter ${key} of template ${template.name}`);
      // a list of components written as objects: items by their own $unnyId (10.4, Q-L-22)
      if (p.type === 'list' && p.of && p.of.type === 'document' && isComponentTarget(catalog, p.of) && Array.isArray(value) && value.some((v) => v && typeof v === 'object')) {
        const current = Array.isArray((doc.data as Record<string, Json>)[p.name]) ? ((doc.data as Record<string, Json>)[p.name] as Json[]) : [];
        const kept = new Set<string>();
        const ids: string[] = [];
        for (const item of value) {
          if (item && typeof item === 'object') {
            const o = item as Obj & { $unnyId?: string; $type?: string };
            const existingId = o.$unnyId ? writeCtx.documentIdFor(o.$unnyId) : null;
            if (existingId) {
              await updateModel({ ...o, $unnyId: existingId, $type: o.$type ?? (resolveLocal(existingId)?.template ?? '') });
              kept.add(existingId);
              ids.push(existingId);
            } else {
              const created = await createModel({ ...o, $type: o.$type ?? p.of.template ?? '' });
              ids.push(created.$id);
              kept.add(created.$id);
            }
          } else if (typeof item === 'string') {
            const id = writeCtx.documentIdFor(item);
            if (id) {
              ids.push(id);
              kept.add(id);
            }
          }
        }
        for (const old of current) {
          if (typeof old === 'string' && !kept.has(old)) {
            const node = resolveLocal(old) as Document | undefined;
            if (node) workspace.remove('document', old, isTempId(old) ? undefined : baseOf(node), { label: label(node), template: node.template, actor });
          }
        }
        editField(doc, p, ids as unknown as Json);
        continue;
      }
      // a single component written as an object: update it in place (or create it)
      if (p.type === 'document' && isComponentTarget(catalog, p) && value && typeof value === 'object') {
        const o = value as Obj & { $unnyId?: string; $type?: string };
        const existingId = o.$unnyId ? writeCtx.documentIdFor(o.$unnyId) : null;
        if (existingId) {
          await updateModel({ ...o, $unnyId: existingId, $type: o.$type ?? (resolveLocal(existingId)?.template ?? '') });
          editField(doc, p, existingId);
        } else {
          const created = await createModel({ ...o, $type: o.$type ?? p.template ?? '' });
          editField(doc, p, created.$id);
        }
        continue;
      }
      editField(doc, p, normalizeValue(writeCtx, p, value));
    }
  };

  const createModel = async (input: unknown): Promise<Expanded> => {
    if (typeof input === 'string') throw new ApiCallError(`expected an object with $type, got string. Hint: pass the parsed object, not a JSON string.`);
    if (!input || typeof input !== 'object') throw new ApiCallError(`expected an object with $type, got ${input === null ? 'null' : typeof input}`);
    const o = input as Obj & { $type?: unknown };
    if (typeof o.$type !== 'string' || !o.$type) throw new ApiCallError('createEntity requires $type in expandedJson');
    const template = templateOf(o.$type);
    const id = createDocumentDraft(workspace, template, {});
    const draft = workspace.getDraft(entityKey('document', id));
    if (draft) {
      draft.actor = actor;
      draft.label = label(draft.current as unknown as Document);
    }
    const doc = (workspace.getDraft(entityKey('document', id))?.current as unknown as Document) ?? ({ id, template: template.name, data: {}, unnyId: null } as unknown as Document);
    await applyFields(doc, template, o);
    const after = (workspace.getDraft(entityKey('document', id))?.current as unknown as Document) ?? doc;
    return expandDocument(expandCtx, after);
  };

  const updateModel = async (input: unknown): Promise<Expanded | null> => {
    if (typeof input === 'string') throw new ApiCallError(`expected an object with $type, got string. Hint: pass the parsed object, not a JSON string.`);
    if (!input || typeof input !== 'object') throw new ApiCallError(`expected an object with $unnyId, got ${input === null ? 'null' : typeof input}`);
    const o = input as Obj & { $unnyId?: unknown; $type?: unknown };
    if (typeof o.$unnyId !== 'string') throw new ApiCallError(`expected $unnyId to be a string, got ${o.$unnyId === undefined ? 'undefined' : typeof o.$unnyId}`);
    const doc = await findDocument(o.$unnyId);
    if (!doc) return null;
    if (typeof o.$type === 'string' && o.$type && o.$type !== doc.template) {
      const declared = resolveTemplateName(catalog, o.$type);
      if (!declared || declared.name !== doc.template) throw new ApiCallError(`$type mismatch: expected ${doc.template}, got ${o.$type}`);
    }
    const template = catalog.templates.get(doc.template);
    if (!template) throw new ApiCallError(`Unknown template: ${doc.template}`);
    await applyFields(doc, template, o);
    const after = (workspace.getDraft(entityKey('document', doc.id))?.current as unknown as Document) ?? doc;
    return expandDocument(expandCtx, after);
  };

  const cms: Record<string, (...args: unknown[]) => Promise<unknown>> = {
    async getModels(name, includeChildren = true) {
      if (typeof name !== 'string' || !name) return [];
      let template: Template | undefined;
      try {
        template = resolveTemplateName(catalog, name);
      } catch (e) {
        throw new ApiCallError(e instanceof Error ? e.message : String(e));
      }
      if (!template) return [];
      const family = familyOf(template, includeChildren !== false);
      const server = await adapter.listDocuments(template.name, includeChildren !== false);
      catalog.resolver.seed(server);
      const rows = overlayDrafts(server, draftsOfFamily(family), family);
      return rows.map((d) => expandDocument(expandCtx, d));
    },
    async getModelByUnnyId(id) {
      if (typeof id !== 'string' || !id) return null;
      const doc = await findDocument(id);
      return doc ? expandDocument(expandCtx, doc) : null;
    },
    async getTemplateList(name) {
      if (typeof name === 'string' && name) {
        const t = resolveTemplateName(catalog, name);
        if (!t) return [];
        return [t, ...heirsOf(catalog, t.name)].map((x) => describeTemplate(catalog, x));
      }
      return catalog.templateList.map((x) => describeTemplate(catalog, x));
    },
    createModel,
    updateModel,
    async updateModels(docs) {
      if (!Array.isArray(docs)) throw new ApiCallError('updateModels expects an array');
      const out: unknown[] = [];
      for (const d of docs) out.push(await updateModel(d));
      return out;
    },
    async cloneModel(id) {
      if (typeof id !== 'string') throw new ApiCallError(`expected $unnyId to be a string, got ${typeof id}`);
      const doc = await findDocument(id);
      if (!doc) return null;
      const before = new Set(workspace.all.map((d) => d.key));
      const newId = cloneDocument(workspace, catalog, doc);
      // the clone and the owned components it copied belong to the extension's actor group (10.4)
      for (const d of workspace.all) if (!before.has(d.key)) d.actor = actor;
      const draft = workspace.getDraft(entityKey('document', newId));
      const created = (draft?.current as unknown as Document) ?? doc;
      return expandDocument(expandCtx, created);
    },
  };

  const locString = (s: { id: string; group: string; key: string; description?: string | null; export?: boolean; values?: Record<string, string> | null }) => ({ id: s.id, guid: s.id, key: s.key, group: s.group, description: s.description ?? null, translations: s.values ?? {}, export: s.export ?? true });
  const allStrings = () => {
    const byId = new Map(catalog.stringList.map((s) => [s.id, { id: s.id, group: s.group, key: s.key, description: s.description, export: s.export, values: (s.values ?? {}) as Record<string, string> }]));
    for (const d of workspace.all) {
      if (d.kind !== 'string') continue;
      if (d.status === 'deleted') byId.delete(d.id);
      else {
        const c = d.current as { group: string; key: string; description?: string | null; export?: boolean; values?: Record<string, string> };
        byId.set(d.id, { id: d.id, group: c.group, key: c.key, description: c.description ?? null, export: c.export ?? true, values: c.values ?? {} });
      }
    }
    return Array.from(byId.values());
  };
  const stringByKey = (fullKey: string) => allStrings().find((s) => `${s.group}/${s.key}` === fullKey);
  const localization: Record<string, (...args: unknown[]) => Promise<unknown>> = {
    async getGroups() {
      return Array.from(new Set(allStrings().map((s) => s.group))).sort().map((path) => ({ path }));
    },
    async getStrings(group) {
      return allStrings()
        .filter((s) => typeof group !== 'string' || !group || s.group === group)
        .map(locString);
    },
    async getStringByKey(key) {
      if (typeof key !== 'string' || !key.includes('/')) throw new ApiCallError('A localization key is "GROUP/KEY".');
      const s = stringByKey(key);
      return s ? locString(s) : null;
    },
    async getLanguages() {
      return catalog.languages.map((l) => ({ code: l.code, name: l.name, localizedName: null, enabled: true }));
    },
    async createGroup(path) {
      if (typeof path !== 'string' || !path.trim()) throw new ApiCallError('A group needs a name.');
      return { path: path.trim() };
    },
    async deleteGroup(path) {
      if (allStrings().some((s) => s.group === path)) throw new ApiCallError(`Group ${String(path)} has strings.`);
      return { success: true };
    },
    async createString(input) {
      const o = (input ?? {}) as { key?: unknown; group?: unknown; translations?: unknown; description?: unknown };
      if (typeof o.key !== 'string' || typeof o.group !== 'string') throw new ApiCallError('createString needs { key, group }.');
      const fullKey = `${o.group}/${o.key}`;
      if (stringByKey(fullKey)) throw new ApiCallError(`Key ${fullKey} already exists.`);
      const values = (o.translations && typeof o.translations === 'object' ? (o.translations as Record<string, string>) : {}) as Record<string, string>;
      const id = workspace.create('string', { id: '', group: o.group, key: o.key, description: typeof o.description === 'string' ? o.description : null, export: true, values } as unknown as Json, { label: fullKey, template: 'Localization', actor });
      return locString({ id, group: o.group, key: o.key, description: typeof o.description === 'string' ? o.description : null, export: true, values });
    },
    async updateString(input) {
      const o = (input ?? {}) as { key?: unknown; translations?: unknown; description?: unknown; newKey?: unknown; newGroup?: unknown };
      if (typeof o.key !== 'string') throw new ApiCallError('updateString needs { key: "GROUP/KEY" }.');
      const s = stringByKey(o.key);
      if (!s) throw new ApiCallError(`Key ${o.key} not found.`);
      const server = catalog.strings.get(s.id);
      const meta = { base: server ? { version: Number(server.version), value: server as unknown as Json } : undefined, label: o.key, template: 'Localization', actor };
      if (o.translations && typeof o.translations === 'object') {
        for (const [code, text] of Object.entries(o.translations as Record<string, string>)) {
          if (!catalog.languages.some((l) => l.code === code)) throw new ApiCallError(`Unknown language code ${code}.`);
          workspace.edit('string', s.id, `values.${code}`, text, meta);
        }
      }
      if (typeof o.description === 'string') workspace.edit('string', s.id, 'description', o.description, meta);
      if (typeof o.newKey === 'string') workspace.edit('string', s.id, 'key', o.newKey, meta);
      if (typeof o.newGroup === 'string') workspace.edit('string', s.id, 'group', o.newGroup, meta);
      const after = allStrings().find((x) => x.id === s.id)!;
      return locString(after);
    },
    async deleteString(key) {
      if (typeof key !== 'string') throw new ApiCallError('deleteString needs "GROUP/KEY".');
      const s = stringByKey(key);
      if (!s) throw new ApiCallError(`Key ${key} not found.`);
      const server = catalog.strings.get(s.id);
      workspace.remove('string', s.id, server ? { version: Number(server.version), value: server as unknown as Json } : undefined, { label: key, template: 'Localization', actor });
      return { success: true };
    },
  };

  const TYPE_WORDS: Record<number, string> = { 1: 'Consumable', 2: 'NonConsumable', 3: 'Subscription' };
  const typeNumber = (t: unknown) => (typeof t === 'number' ? t : t === 'NonConsumable' ? 2 : t === 'Subscription' ? 3 : 1);
  const productOut = (p: { id: string; itemId: string; name: string; description?: string | null; type: number | string; price: { amount: number | string; currency: string }; platform: string; localizedName?: string | null; localizedDescription?: string | null; iconUrl?: string | null }) => ({
    id: p.id,
    itemId: p.itemId,
    name: p.name,
    description: p.description ?? null,
    type: TYPE_WORDS[Number(p.type)] ?? 'Consumable',
    price: { amount: Number(p.price.amount), currency: p.price.currency },
    platform: p.platform,
    localizedName: p.localizedName ? (catalog.strings.get(p.localizedName) ? `${catalog.strings.get(p.localizedName)!.group}/${catalog.strings.get(p.localizedName)!.key}` : p.localizedName) : null,
    localizedDescription: p.localizedDescription ? (catalog.strings.get(p.localizedDescription) ? `${catalog.strings.get(p.localizedDescription)!.group}/${catalog.strings.get(p.localizedDescription)!.key}` : p.localizedDescription) : null,
    iconUrl: p.iconUrl ?? null,
  });
  const allProducts = () => {
    const byId = new Map(catalog.productList.map((p) => [p.id, p as unknown as Parameters<typeof productOut>[0]]));
    for (const d of workspace.all) {
      if (d.kind !== 'product') continue;
      if (d.status === 'deleted') byId.delete(d.id);
      else byId.set(d.id, d.current as unknown as Parameters<typeof productOut>[0]);
    }
    return Array.from(byId.values());
  };
  const products: Record<string, (...args: unknown[]) => Promise<unknown>> = {
    async getProducts() {
      return allProducts().map(productOut);
    },
    async getProduct(itemId) {
      const p = allProducts().find((x) => x.itemId === itemId && x.platform === 'default') ?? allProducts().find((x) => x.itemId === itemId);
      return p ? productOut(p) : null;
    },
    async createProduct(input) {
      const o = (input ?? {}) as Obj;
      if (typeof o.itemId !== 'string' || !o.itemId) throw new ApiCallError('createProduct needs an itemId.');
      const price = (o.price && typeof o.price === 'object' ? o.price : { amount: 0, currency: 'USD' }) as { amount?: number; currency?: string };
      const id = workspace.create(
        'product',
        { id: '', itemId: o.itemId, name: typeof o.name === 'string' ? o.name : o.itemId, description: typeof o.description === 'string' ? o.description : null, price: { amount: Number(price.amount ?? 0), currency: price.currency ?? 'USD' }, platform: typeof o.platform === 'string' ? o.platform : 'default', type: typeNumber(o.type), baseId: typeof o.baseId === 'string' ? o.baseId : null, iconUrl: typeof o.iconUrl === 'string' ? o.iconUrl : null, localizedName: typeof o.localizedName === 'string' ? writeCtx.stringIdFor(o.localizedName) : null, localizedDescription: typeof o.localizedDescription === 'string' ? writeCtx.stringIdFor(o.localizedDescription) : null } as unknown as Json,
        { label: o.itemId, template: 'Products', actor },
      );
      const created = allProducts().find((x) => x.id === id)!;
      return productOut(created);
    },
    async updateProduct(input) {
      const o = (input ?? {}) as Obj;
      if (typeof o.itemId !== 'string') throw new ApiCallError('updateProduct needs the itemId.');
      const p = allProducts().find((x) => x.itemId === o.itemId && x.platform === (typeof o.platform === 'string' ? o.platform : 'default')) ?? allProducts().find((x) => x.itemId === o.itemId);
      if (!p) throw new ApiCallError(`Unknown product '${o.itemId}'.`);
      const server = catalog.products.get(p.id);
      const meta = { base: server ? { version: Number(server.version), value: server as unknown as Json } : undefined, label: p.itemId, template: 'Products', actor };
      for (const [k, v] of Object.entries(o)) {
        if (k === 'itemId' || k === 'id') continue;
        if (k === 'type') workspace.edit('product', p.id, 'type', typeNumber(v), meta);
        else if (k === 'localizedName' || k === 'localizedDescription') workspace.edit('product', p.id, k, typeof v === 'string' ? writeCtx.stringIdFor(v) : null, meta);
        else if (['name', 'description', 'price', 'platform', 'baseId', 'iconUrl'].includes(k)) workspace.edit('product', p.id, k, v as Json, meta);
      }
      return productOut(allProducts().find((x) => x.id === p.id)!);
    },
    async deleteProduct(itemId) {
      const p = allProducts().find((x) => x.itemId === itemId);
      if (!p) throw new ApiCallError(`Unknown product '${String(itemId)}'.`);
      const server = catalog.products.get(p.id);
      workspace.remove('product', p.id, server ? { version: Number(server.version), value: server as unknown as Json } : undefined, { label: p.itemId, template: 'Products', actor });
      return { success: true };
    },
  };

  type AssetLike = { id: string; unnyId?: string | null; name: string; kind: string; extension?: string | null; contentPath?: string | null; group?: string | null };
  const objectOut = (a: AssetLike, signed: Record<string, string | undefined>) => ({ id: a.id, unnyId: a.unnyId ?? null, name: a.name, type: a.kind, ext: a.extension ? `.${a.extension}` : '', fileUrl: a.contentPath ? (signed[a.id] ?? (adapter.assetUrls ? null : a.contentPath)) : null, group: a.group ?? null });
  /** the objects with their `fileUrl` signed in one call (the ones with content) */
  const objectsOut = async (list: AssetLike[]) => {
    const ids = list.filter((a) => a.contentPath).map((a) => a.id);
    let signed: Record<string, string | undefined> = {};
    if (adapter.assetUrls && ids.length) {
      try {
        signed = await adapter.assetUrls(ids);
      } catch {
        signed = {};
      }
    }
    return list.map((a) => objectOut(a, signed));
  };
  const objects: Record<string, (...args: unknown[]) => Promise<unknown>> = {
    async getObjects(type) {
      return objectsOut(catalog.assetList.filter((a) => typeof type !== 'string' || !type || a.kind.toLowerCase() === type.toLowerCase()));
    },
    async getObject(id) {
      const a = catalog.assets.get(String(id)) ?? catalog.assetList.find((x) => x.unnyId === String(id));
      return a ? (await objectsOut([a]))[0]! : null;
    },
    async findObjectsByName(name) {
      return objectsOut(catalog.assetList.filter((a) => a.name === name));
    },
  };

  const namespaces: Record<string, Record<string, (...args: unknown[]) => Promise<unknown>>> = { CMS: cms, Localization: localization, Products: products, Objects: objects };
  return async (method, args) => {
    const [ns, fn] = method.split('.') as [string, string];
    const impl = namespaces[ns]?.[fn];
    if (!impl) throw new ApiCallError(`Unknown API method ${method}`);
    return impl(...args);
  };
}

export { unnyIdOf };
